import React from 'react';
import "./smile-pages.css";

export default function HowDoesSmilingIncreaseHappiness() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>How does Smiling Increase Happiness?</h1>

      <p>
        Smiling increases happiness by creating a <strong>positive feedback loop</strong> between your facial
        muscles, brain, and emotions. This loop activates brain systems that regulate mood, reduce stress,
        and enhance well-being—<strong>even if the smile starts out as forced or fake.</strong>
      </p>

      <p>Here’s how it works, backed by neuroscience and psychological research:</p>

      <hr />

      {/* SECTION 1 */}
      <h3>😊 Why Smiling Increases Happiness</h3>

      <h4>1. Facial Feedback Loop</h4>
      <ul>
        <li>
          The <strong>facial feedback hypothesis</strong> suggests that smiling doesn’t just reflect happiness—
          <strong>it creates it</strong>.
        </li>
        <li>
          When you smile, the movement of facial muscles sends signals to your brain that trigger the emotional
          experience of joy.
        </li>
      </ul>

      <p><strong>📚 Study:</strong><br />
      Strack, Martin & Stepper (1988) – People who were tricked into smiling (by holding a pen in their teeth)
      rated cartoons as funnier than those who didn’t smile.</p>

      <hr />

      {/* SECTION 2 */}
      <h4>2. Neurochemical Changes in the Brain</h4>

      <p>Smiling stimulates the release of mood-enhancing brain chemicals.</p>

      <table className="table">
        <thead>
          <tr>
            <th>Neurochemical</th>
            <th>Effect on Mood</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Dopamine</td>
            <td>Increases motivation and pleasure</td>
          </tr>
          <tr>
            <td>Endorphins</td>
            <td>Acts as a natural painkiller</td>
          </tr>
          <tr>
            <td>Serotonin</td>
            <td>Boosts feelings of calm and happiness</td>
          </tr>
        </tbody>
      </table>

      <ul>
        <li>
          These neurochemicals <strong>elevate your mood almost instantly</strong>, reinforcing a sense of happiness.
        </li>
      </ul>

      <p><strong>📚 Source:</strong><br />
      Niedenthal, P. (2007). <em>Embodied Emotion.</em> Current Directions in Psychological Science.</p>

      <hr />

      {/* SECTION 3 */}
      <h4>3. Reduces Stress and Cortisol</h4>

      <ul>
        <li>Smiling lowers levels of <strong>cortisol</strong>, the hormone associated with stress.</li>
        <li>
          When cortisol drops, your brain feels more relaxed, which opens the door to more
          <strong> positive emotions</strong> like happiness.
        </li>
      </ul>

      <p><strong>📚 Source:</strong><br />
      Kraft & Pressman (2012) – Smiling during stressful tasks reduced physiological stress markers.</p>

      <hr />

      {/* SECTION 4 */}
      <h4>4. Smiling Encourages Social Connection</h4>

      <ul>
        <li>
          Smiling makes you more approachable, increasing the likelihood of
          <strong> positive social interactions</strong>.
        </li>
        <li>
          Social connection is a strong predictor of happiness and life satisfaction.
        </li>
      </ul>

      <p><strong>📚 Study:</strong><br />
      Keltner & Bonanno (1997) – Duchenne smiles (genuine smiles) during grief were linked to better social
      support and long-term well-being.</p>

      <hr />

      {/* SUMMARY TABLE */}
      <h3>✅ Summary: How Smiling Increases Happiness</h3>

      <table className="table">
        <thead>
          <tr>
            <th>Mechanism</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Facial feedback loop</td>
            <td><a>Tells</a> your brain you're happy</td>
          </tr>
          <tr>
            <td>Releases “happy” chemicals</td>
            <td>Boosts mood and reduces negativity</td>
          </tr>
          <tr>
            <td>Lowers stress hormones</td>
            <td>Reduces anxiety and emotional tension</td>
          </tr>
          <tr>
            <td>Enhances social bonding</td>
            <td>Increases support and emotional warmth</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* KEYS TO REMEMBER */}
      <h3>🔑 Keys to Remember</h3>

      <p>
        <strong>
          Smiling makes you happier by activating brain circuits that enhance mood, reduce stress,
          and foster connection.
        </strong>
        {' '}Even if smiling starts as a simple facial movement, the emotional benefits are real and measurable.
        Smiling isn’t just a reflection of happiness—<strong>smiling actively helps create happiness.</strong>
      </p>
      <p>
        <strong> That Smile App is here to help you feel happier.</strong>
      </p>
    </div>
  );
}
